import * as Sentry from '@sentry/react';

import { isGeneratedCodingPracticeTest1Enabled } from 'bundles/assignment-submission/FeatureFlags';
import LceCourseExperienceExperiments from 'bundles/epic/clients/LceCourseExperience';
import type { Item } from 'bundles/learner-progress/types/Item';

type IsCourseFrameEnabledParams = {
  /**
   * Currently opened item in the Course Frame/Item Page.
   */
  computedItem?: Item;
};

/**
 * Course Frame has been rolled out and isn't rolling back. However, the generated coding practice test still uses the old frame.
 * Clean this up old code paths after generated coding practice is done with them: https://coursera.atlassian.net/browse/CE-7113
 */
export const isCourseFrameEnabled = ({ computedItem }: IsCourseFrameEnabledParams): boolean => {
  // In case of ungraded generated coding practice test, return false, as we want to use only the Old Frame for them, and keep it out of the A/B test.
  // TODO: clean this up after generated coding practice is done with them: https://coursera.atlassian.net/browse/CE-7113
  if (computedItem) {
    const { courseId, id: itemId, contentSummary } = computedItem;

    const isUngradedAssignment = contentSummary.typeName === 'ungradedAssignment';

    const isCodingPracticeTest1Enabled = isGeneratedCodingPracticeTest1Enabled(courseId, itemId);

    if (isCodingPracticeTest1Enabled && isUngradedAssignment) {
      return false;
    }
  }

  return true;
};

/**
 * EPIC (Config):   https://tools.coursera.org/epic/experiment/0NPe57oZEfC-lAr_4rnFJw returns a string in ISO format "2025-10-05T19:00:00.000Z"
 * @param enrollmentTime - The enrollment time of the user.
 * @returns true if the user is enrolled into a course after the experiment start date.
 */
export const isEnrolledAfterExperimentStartDate = (enrollmentTime?: number): boolean => {
  const startDateString = LceCourseExperienceExperiments.preview('CourseFramePhase3StartDate');
  if (startDateString && typeof enrollmentTime === 'number') {
    const experimentStartDate = new Date(startDateString);
    if (isNaN(experimentStartDate.getTime())) {
      // invalid date from Epic config
      Sentry.captureException('isEnrolledAfterExperimentStartDate: Invalid date from Epic config', {
        extra: { startDateString },
      });
      return false;
    }
    const experimentStartTime = experimentStartDate.getTime();
    return enrollmentTime > experimentStartTime;
  }
  return false;
};

/**
 * EPIC: https://tools.coursera.org/epic/experiment/DJASFX-NEfC-lAr_4rnFJw
 * @returns true if phase 3 should be shown in the UI.
 */
export const isCourseFramePhase3Enabled = (enrollmentTime?: number): boolean => {
  // We should target new course enrollments, then check the experiment variant (with impression point).
  return (
    isEnrolledAfterExperimentStartDate(enrollmentTime) &&
    LceCourseExperienceExperiments.get('isCourseFramePhase3Enabled') === true
  );
};

/**
 * EPIC: https://tools.coursera.org/epic/experiment/5_jNsLNgEfC-lAr_4rnFJw
 * @returns true if phase 4 (right panel) should be shown in the UI.
 */
export const isCourseFramePhase4Enabled = (): boolean => {
  return LceCourseExperienceExperiments.get('isFrameM4Enabled') === true;
};
